import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { openDB, deleteDB } from 'idb';
import { useAppAuth } from './AppAuthContext';
import { useMobileVersion } from './MobileVersionContext';

// File index types
interface FileInfo {
  path: string;
  size: number;
  lastModified: number;
}

interface FileIndex {
  [fileName: string]: FileInfo[];
}

interface FolderIndexData {
  folderName: string;
  folderHandle: FileSystemDirectoryHandle;
  fileIndex: FileIndex;
  lastUpdated: number;
  isUpdating: boolean;
}

interface FolderAccessContextType {
  folderHandles: FileSystemDirectoryHandle[];
  folderAccessGranted: boolean;
  addFolder: () => Promise<void>;
  removeFolder: (name: string) => Promise<void>;
  refreshFolders: () => Promise<void>;
  // Manual popup control
  showFolderAccessPopup: () => void;
  hideFolderAccessPopup: () => void;
  isPopupVisible: boolean;
  // New folder indexing functionality
  getFolderIndex: (folderHandle: FileSystemDirectoryHandle) => FileIndex | null;
  findFileRelativePath: (folderHandle: FileSystemDirectoryHandle, fileName: string) => string | null;
  refreshFolderIndex: (folderHandle: FileSystemDirectoryHandle) => Promise<void>;
  isIndexUpdating: (folderHandle: FileSystemDirectoryHandle) => boolean;
  // New debugging and management functions
  printFolderIndex: (folderHandle: FileSystemDirectoryHandle) => void;
  updateAllFolderIndices: () => Promise<void>;
  clearIndexDatabase: () => Promise<void>;
  cleanIndexDatabase: () => Promise<void>;
}

const FolderAccessContext = createContext<FolderAccessContextType | undefined>(undefined);

const FOLDER_DB = 'file-uploader-db';
const FOLDER_STORE = 'folder-handles';
const INDEX_DB = 'folder-index-db';
const INDEX_STORE = 'folder-indices';
const DB_VERSION = 2; // Increment version to force database upgrade

// Update interval for folder indices (5 minutes)
const UPDATE_INTERVAL = 5 * 60 * 1000;

export const FolderAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [folderHandles, setFolderHandles] = useState<FileSystemDirectoryHandle[]>([]);
  // const [, setShouldPrompt] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [folderIndices, setFolderIndices] = useState<Map<string, FolderIndexData>>(new Map());
  const { hasAccess } = useAppAuth();
  const { deviceType, isEmergencyDesktopMode } = useMobileVersion();
  
  // Ref to track update intervals
  const updateIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Helper function to get folder key
  const getFolderKey = (folderHandle: FileSystemDirectoryHandle): string => {
    return `${folderHandle.name}_${folderHandle.kind}`;
  };

  // Helper function to list files in a directory
  // const _listFilesInDirectory = async (dir: FileSystemDirectoryHandle): Promise<string[]> => {
  //   const files: string[] = [];
  //   try {
  //     for await (const entry of dir.values()) {
  //       if (entry.kind === 'file') {
  //         files.push(entry.name);
  //       }
  //     }
  //   } catch (err) {
  //     console.error('Error listing directory contents:', err);
  //   }
  //   return files;
  // };

  // Build folder index recursively
  const buildFolderIndex = async (folderHandle: FileSystemDirectoryHandle): Promise<FileIndex> => {
    const index: FileIndex = {};
    
    const scanDirectory = async (dir: FileSystemDirectoryHandle, currentPath = '') => {
      try {
        // Scan files in current directory
        for await (const entry of dir.values()) {
          if (entry.kind === 'file') {
            const fileHandle = entry as FileSystemFileHandle;
            const file = await fileHandle.getFile();
            const fileName = fileHandle.name;
            
            if (!index[fileName]) {
              index[fileName] = [];
            }
            
            index[fileName].push({
              path: currentPath || 'root',
              size: file.size,
              lastModified: file.lastModified
            });
          }
        }
        
        // Recursively scan subdirectories
        for await (const entry of dir.values()) {
          if (entry.kind === 'directory') {
            const newPath = currentPath ? `${currentPath}/${entry.name}` : entry.name;
            await scanDirectory(await dir.getDirectoryHandle(entry.name), newPath);
          }
        }
      } catch (err) {
        console.error(`Error scanning directory ${currentPath}:`, err);
      }
    };
    
    await scanDirectory(folderHandle);
    return index;
  };

  // Save folder index to IndexedDB
  const saveFolderIndex = async (folderKey: string, indexData: FolderIndexData) => {
    try {
      const db = await openDB(INDEX_DB, DB_VERSION, {
        upgrade(db, oldVersion, newVersion) {
          // Create the index store if it doesn't exist
          if (!db.objectStoreNames.contains(INDEX_STORE)) {
            db.createObjectStore(INDEX_STORE);
          }
        },
      });
      await db.put(INDEX_STORE, indexData, folderKey);
    } catch (err) {
      console.error('Error saving folder index:', err);
    }
  };

  // Load folder index from IndexedDB
  const loadFolderIndex = async (folderKey: string): Promise<FolderIndexData | null> => {
    try {
      const db = await openDB(INDEX_DB, DB_VERSION, {
        upgrade(db, oldVersion, newVersion) {
          // Create the index store if it doesn't exist
          if (!db.objectStoreNames.contains(INDEX_STORE)) {
            db.createObjectStore(INDEX_STORE);
          }
        },
      });
      const indexData = await db.get(INDEX_STORE, folderKey);
      return indexData || null;
    } catch (err) {
      console.error('Error loading folder index:', err);
      return null;
    }
  };

  // Refresh folder index
  const refreshFolderIndex = useCallback(async (folderHandle: FileSystemDirectoryHandle) => {
    const folderKey = getFolderKey(folderHandle);
    
    // Mark as updating
    setFolderIndices(prev => new Map(prev).set(folderKey, {
      folderName: folderHandle.name,
      folderHandle,
      fileIndex: {},
      lastUpdated: Date.now(),
      isUpdating: true
    }));

    try {
      const fileIndex = await buildFolderIndex(folderHandle);
      const indexData: FolderIndexData = {
        folderName: folderHandle.name,
        folderHandle,
        fileIndex,
        lastUpdated: Date.now(),
        isUpdating: false
      };

      // Update state
      setFolderIndices(prev => new Map(prev).set(folderKey, indexData));
      
      // Save to storage
      await saveFolderIndex(folderKey, indexData);
    } catch (err) {
      console.error(`❌ Failed to refresh index for ${folderHandle.name}:`, err);
      // Mark as not updating on error
      setFolderIndices(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(folderKey);
        if (existing) {
          newMap.set(folderKey, { ...existing, isUpdating: false });
        }
        return newMap;
      });
    }
  }, [setFolderIndices]);

  // Initialize folder index
  const initializeFolderIndex = useCallback(async (folderHandle: FileSystemDirectoryHandle) => {
    const folderKey = getFolderKey(folderHandle);
    
    // Check if we already have this folder indexed
    if (folderIndices.has(folderKey)) {
      return;
    }

    // Try to load from storage first
    const savedIndex = await loadFolderIndex(folderKey);
    if (savedIndex && savedIndex.folderHandle) {
      // Verify the handle is still valid
      try {
        await savedIndex.folderHandle.requestPermission?.({ mode: 'readwrite' });
        setFolderIndices(prev => new Map(prev).set(folderKey, {
          ...savedIndex,
          isUpdating: false
        }));
        return;
      } catch (err) {
        // Cached index invalid, will rebuild
      }
    }

    // Build new index
    await refreshFolderIndex(folderHandle);
  }, [folderIndices, refreshFolderIndex, setFolderIndices]);

  // Get folder index
  const getFolderIndex = (folderHandle: FileSystemDirectoryHandle): FileIndex | null => {
    const folderKey = getFolderKey(folderHandle);
    const indexData = folderIndices.get(folderKey);
    return indexData?.fileIndex || null;
  };

  // Find file relative path using index
  const findFileRelativePath = (folderHandle: FileSystemDirectoryHandle, fileName: string): string | null => {
    const folderKey = getFolderKey(folderHandle);
    const indexData = folderIndices.get(folderKey);
    
    if (!indexData?.fileIndex) {
      return null;
    }

    const matches = indexData.fileIndex[fileName];
    if (!matches || matches.length === 0) {
      return null;
    }

    if (matches.length === 1) {
      const path = matches[0].path;
      return path === 'root' ? '' : path;
    }

    // Multiple matches - use first one but warn
    console.warn(`⚠️ Found ${matches.length} files with name "${fileName}" in index. Using first match.`);
    const path = matches[0].path;
    return path === 'root' ? '' : path;
  };

  // Check if index is updating
  const isIndexUpdating = (folderHandle: FileSystemDirectoryHandle): boolean => {
    const folderKey = getFolderKey(folderHandle);
    const indexData = folderIndices.get(folderKey);
    return indexData?.isUpdating || false;
  };

  // Print folder index for debugging
  const printFolderIndex = (folderHandle: FileSystemDirectoryHandle): void => {
    const folderKey = getFolderKey(folderHandle);
    const indexData = folderIndices.get(folderKey);
    
    if (!indexData?.fileIndex) {
      console.log(`📂 No index found for folder: ${folderHandle.name}`);
      return;
    }

    console.log(`📂 Folder Index for: ${folderHandle.name}`);
    console.log(`📅 Last Updated: ${new Date(indexData.lastUpdated).toLocaleString()}`);
    console.log(`📊 Total Unique Files: ${Object.keys(indexData.fileIndex).length}`);
    console.log('📁 File Hierarchy:');
    
    // Group files by path for better visualization
    const filesByPath: { [path: string]: string[] } = {};
    Object.entries(indexData.fileIndex).forEach(([fileName, fileInfos]) => {
      fileInfos.forEach(fileInfo => {
        const path = fileInfo.path === 'root' ? '/' : `/${fileInfo.path}`;
        if (!filesByPath[path]) {
          filesByPath[path] = [];
        }
        filesByPath[path].push(fileName);
      });
    });

    // Print organized hierarchy
    Object.keys(filesByPath).sort().forEach(path => {
      console.log(`  ${path === '/' ? '📁 Root' : `📁 ${path}`}`);
      filesByPath[path].sort().forEach(fileName => {
        console.log(`    📄 ${fileName}`);
      });
    });

    // Print duplicate files warning
    const duplicates = Object.entries(indexData.fileIndex).filter(([_, fileInfos]) => fileInfos.length > 1);
    if (duplicates.length > 0) {
      console.log('⚠️  Duplicate filenames found:');
      duplicates.forEach(([fileName, fileInfos]) => {
        console.log(`    ${fileName} (${fileInfos.length} instances):`);
        fileInfos.forEach(fileInfo => {
          const path = fileInfo.path === 'root' ? '/' : `/${fileInfo.path}`;
          console.log(`      - ${path}`);
        });
      });
    }
  };

  // Update all folder indices
  const updateAllFolderIndices = async (): Promise<void> => {
    const updatePromises = folderHandles.map(async (folderHandle) => {
      try {
        await refreshFolderIndex(folderHandle);
      } catch (error) {
        console.error(`❌ Failed to update index for: ${folderHandle.name}`, error);
      }
    });
    
    await Promise.all(updatePromises);
  };

  // Clear database if there are persistent issues (for debugging)
  const clearIndexDatabase = async (): Promise<void> => {
    try {
      // Delete the old database
      await deleteDB(INDEX_DB);
      console.log('Index database cleared successfully');
    } catch (err) {
      console.error('Error clearing index database:', err);
    }
  };

  // Clean IndexDB by removing orphaned folder indices
  const cleanIndexDatabase = async (): Promise<void> => {
    try {
      const db = await openDB(INDEX_DB, DB_VERSION, {
        upgrade(db, oldVersion, newVersion) {
          // Create the index store if it doesn't exist
          if (!db.objectStoreNames.contains(INDEX_STORE)) {
            db.createObjectStore(INDEX_STORE);
          }
        },
      });

      // Get all keys in the index store
      const allKeys = await db.getAllKeys(INDEX_STORE);
      console.log(`Found ${allKeys.length} entries in index database`);

      // Get current folder keys
      const currentFolderKeys = folderHandles.map(handle => getFolderKey(handle));
      console.log(`Current folder keys:`, currentFolderKeys);

      // Find orphaned keys (keys that exist in DB but not in current folders)
      const orphanedKeys = allKeys.filter(key => !currentFolderKeys.includes(key as string));
      console.log(`Found ${orphanedKeys.length} orphaned entries to remove`);

      // Remove orphaned entries
      for (const orphanedKey of orphanedKeys) {
        await db.delete(INDEX_STORE, orphanedKey);
        console.log(`Removed orphaned index for: ${orphanedKey}`);
      }

      // Also clean up the in-memory indices
      setFolderIndices(prev => {
        const newMap = new Map(prev);
        newMap.forEach((_, key) => {
          if (!currentFolderKeys.includes(key)) {
            newMap.delete(key);
            console.log(`Removed orphaned in-memory index for: ${key}`);
          }
        });
        return newMap;
      });

      console.log(`Index database cleaned successfully. Removed ${orphanedKeys.length} orphaned entries.`);
    } catch (err) {
      console.error('Error cleaning index database:', err);
    }
  };

  // Setup periodic updates for a folder
  const setupPeriodicUpdates = useCallback((folderHandle: FileSystemDirectoryHandle) => {
    const folderKey = getFolderKey(folderHandle);
    
    // Clear existing interval
    const existingInterval = updateIntervalsRef.current.get(folderKey);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Setup new interval
    const interval = setInterval(async () => {
      const indexData = folderIndices.get(folderKey);
      if (indexData && Date.now() - indexData.lastUpdated > UPDATE_INTERVAL) {
        await refreshFolderIndex(folderHandle);
      }
    }, UPDATE_INTERVAL);

    updateIntervalsRef.current.set(folderKey, interval);
  }, [folderIndices, refreshFolderIndex]);

  // Load all folder handles from IndexedDB
  const refreshFolders = useCallback(async () => {
    try {
      const db = await openDB(FOLDER_DB, 1, {
        upgrade(db) {
          db.createObjectStore(FOLDER_STORE);
        },
      });
      const keys = await db.getAllKeys(FOLDER_STORE);
      const handles: FileSystemDirectoryHandle[] = [];
      for (const key of keys) {
        const handle = await db.get(FOLDER_STORE, key);
        if (handle) {
          // Do not request permission during background refresh.
          // requestPermission requires explicit user activation and throws otherwise.
          const perm = await handle.queryPermission?.({ mode: 'readwrite' }) ?? 'denied';
          if (perm === 'granted') {
            handles.push(handle);
          }
        }
      }
      setFolderHandles(handles);
      // Don't automatically show popup - let user control it manually
    } catch (err) {
      console.error('Error loading folders:', err);
      setFolderHandles([]);
      // Don't automatically show popup - let user control it manually
    }
  }, []);

  // Manual popup control functions
  const showFolderAccessPopup = useCallback(() => {
    setShowPopup(true);
  }, []);

  const hideFolderAccessPopup = useCallback(() => {
    setShowPopup(false);
  }, []);

  // Add a new folder
  const addFolder = useCallback(async () => {
    try {
      if (!window.showDirectoryPicker) {
        alert('Your browser does not support the File System Access API.');
        return;
      }
      const handle = await window.showDirectoryPicker();
      const perm = await handle.requestPermission?.({ mode: 'readwrite' }) ?? 'denied';
      if (perm === 'granted') {
        const db = await openDB(FOLDER_DB, 1);
        await db.put(FOLDER_STORE, handle, handle.name);
        await refreshFolders();
        
        // Initialize index for new folder
        await initializeFolderIndex(handle);
        setupPeriodicUpdates(handle);
        
        // Hide popup after successful folder addition
        hideFolderAccessPopup();
      }
    } catch (err) {
      // ignore
    }
  }, [refreshFolders, hideFolderAccessPopup, initializeFolderIndex, setupPeriodicUpdates]);

  // Remove a folder by name
  const removeFolder = useCallback(async (name: string) => {
    const db = await openDB(FOLDER_DB, 1);
    await db.delete(FOLDER_STORE, name);
    
    // Clean up index and intervals
    const folderKey = Array.from(folderIndices.keys()).find(key => key.startsWith(name));
    if (folderKey) {
      const interval = updateIntervalsRef.current.get(folderKey);
      if (interval) {
        clearInterval(interval);
        updateIntervalsRef.current.delete(folderKey);
      }
      
      setFolderIndices(prev => {
        const newMap = new Map(prev);
        newMap.delete(folderKey);
        return newMap;
      });
    }
    
    await refreshFolders();
  }, [refreshFolders, folderIndices]);

  // Initialize indices for existing folders
  useEffect(() => {
    const initializeAllIndices = async () => {
      for (const folderHandle of folderHandles) {
        await initializeFolderIndex(folderHandle);
        setupPeriodicUpdates(folderHandle);
      }
    };
    
    if (folderHandles.length > 0) {
      initializeAllIndices();
    }
  }, [folderHandles, initializeFolderIndex, setupPeriodicUpdates]);

  // Cleanup intervals on unmount
  useEffect(() => {
    const intervals = updateIntervalsRef.current;
    return () => {
      intervals.forEach(interval => clearInterval(interval));
      intervals.clear();
    };
  }, []);

  useEffect(() => {
    refreshFolders();
  }, [refreshFolders]);

  return (
    <FolderAccessContext.Provider value={{
      folderHandles,
      folderAccessGranted: folderHandles.length > 0,
      addFolder,
      removeFolder,
      refreshFolders,
      // Manual popup control
      showFolderAccessPopup,
      hideFolderAccessPopup,
      isPopupVisible: showPopup,
      // New folder indexing functionality
      getFolderIndex,
      findFileRelativePath,
      refreshFolderIndex,
      isIndexUpdating,
      // New debugging and management functions
      printFolderIndex,
      updateAllFolderIndices,
      clearIndexDatabase,
      cleanIndexDatabase,
    }}>
      {children}
      {showPopup && hasAccess('admin-links') && (deviceType === 'desktop' || isEmergencyDesktopMode) && (
        <div style={{ 
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          background: '#E3F2FD', 
          border: '3px solid #2196F3', 
          borderRadius: 10, 
          padding: 32, 
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)', 
          zIndex: 2000,
          maxWidth: 500,
          minWidth: 400
        }}>
          <div style={{ 
            position: 'relative'
          }}>
            <h3 style={{ color: '#1976D2', margin: '0 0 16px 0', fontSize: '20px', fontWeight: 'bold' }}>
              Folder Access (Optional)
            </h3>
            <p style={{ color: '#1565C0', margin: '0 0 24px 0', lineHeight: 1.6 }}>
              Folder access is optional and not required to use this application. You can grant access now or continue without it.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                             <button 
                 onClick={hideFolderAccessPopup}
                 style={{
                   padding: '10px 20px',
                   border: '2px solid #2196F3',
                   backgroundColor: 'transparent',
                   color: '#2196F3',
                   borderRadius: 6,
                   cursor: 'pointer',
                   fontSize: '14px'
                 }}
               >
                 Continue Without Access
               </button>
              <button 
                onClick={addFolder}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Grant Access
              </button>
            </div>
          </div>
        </div>
      )}
    </FolderAccessContext.Provider>
  );
};

export const useFolderAccess = () => {
  const ctx = useContext(FolderAccessContext);
  if (!ctx) throw new Error('useFolderAccess must be used within a FolderAccessProvider');
  return ctx;
}; 